import { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { computePosition, offset, arrow, flip, shift, autoUpdate } from '@floating-ui/dom';
import useIsMobile from '../hooks/useIsMobile';
import useKeyPress from '../hooks/useKeyPress';
import Heading from '../Heading';
import CloseSmall from '@athena/forge-icons/dist/CloseSmall';
import { forgeClassHelper } from '../utils/classes';

const classes = forgeClassHelper('popup');

/** A dialog for disclosing more details. */
const Popover = ({
  targetRef,
  onClose: upstreamOnClose,
  children,
  className,
  isOpen,
  heading,
  hideArrow,
  placement,
  shiftElement,
}) => {
  const isMobile = useIsMobile();
  const tooltipRef = useRef(null);
  const arrowRef = useRef(null);
  const floatingUICleanupRef = useRef();

  /** Close the popover */
  const onClose = useCallback(() => {
    if (floatingUICleanupRef.current) {
      floatingUICleanupRef.current();
      floatingUICleanupRef.current = null;
      upstreamOnClose();
    }
  }, [upstreamOnClose]);

  const ESCAPE_KEY_CODE = 27;
  useKeyPress({ keyCode: ESCAPE_KEY_CODE, shouldListen: isOpen, callback: onClose });

  /** Computing the popover position. */
  const updatePosition = useCallback(() => {
    if (tooltipRef.current) {
      computePosition(targetRef.current, tooltipRef.current, {
        placement: isMobile ? 'bottom' : placement,
        middleware: [
          offset(8),
          shiftElement ? shift() : '',
          flip({ fallbackAxisSideDirection: 'right' }),
          // arrow() should generally be placed toward the end of middleware array
          arrow({ element: arrowRef.current }),
        ],
      }).then(({ x, y, placement, middlewareData }) => {
        if (tooltipRef.current) {
          Object.assign(tooltipRef.current.style, {
            left: `${x}px`,
            top: `${y}px`,
          });
        }

        const side = placement.split('-')[0];
        const staticSide = {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right',
        }[side];

        if (middlewareData.arrow && arrowRef.current) {
          const { x: arrowX, y: arrowY } = middlewareData.arrow;
          Object.assign(arrowRef.current.style, {
            left: arrowX != null ? `${arrowX}px` : '',
            top: arrowY != null ? `${arrowY}px` : '',
            // Ensure the static side gets unset when
            // flipping to other placements' axes.
            right: '',
            bottom: '',
            [staticSide]: '-4px',
          });
        }
      });
    }
    // isOpen is technically not required to be in the dependency array, but its presence is necessary to function properly.
  }, [isOpen, placement, isMobile]);

  useEffect(() => {
    floatingUICleanupRef.current = autoUpdate(targetRef.current, tooltipRef.current, updatePosition);
  }, [updatePosition]);

  return (
    <div
      {...classes({
        element: 'tooltip',
        modifiers: {
          invisible: !isOpen,
        },
        extra: className,
      })}
      ref={tooltipRef}
      role="dialog"
      data-popover-placement={placement}
    >
      <button type="button" {...classes({ element: 'dismiss-button' })} onClick={onClose}>
        <CloseSmall {...classes({ element: 'dismiss' })} />
      </button>
      <div {...classes({ element: 'content' })}>
        {heading && (
          <div
            {...classes({
              element: 'header',
            })}
          >
            <Heading tag="h3" variant="subsection" text={heading} />
          </div>
        )}
        {children}
      </div>
      {!hideArrow && <div {...classes({ element: 'arrow' })} ref={arrowRef} />}
    </div>
  );
};

Popover.propTypes = {
  /** Ref to the Popover's target element. The Popover will be positioned relative to this target element */
  targetRef: PropTypes.any.isRequired,

  /** Function called when the user tries to close. Useful when in controlled mode */
  onClose: PropTypes.func.isRequired,

  /** Content displayed in popover */
  children: PropTypes.any,

  /** CSS class to apply to the Popover's root element */
  className: PropTypes.string,

  /** Set to true to show the popover */
  isOpen: PropTypes.bool,

  /** Heading - this uses the Heading component, with variant set to subsection. */
  heading: PropTypes.string,

  /** Set to true to hide the arrow that points to the Popover's trigger when the Popover is open  */
  hideArrow: PropTypes.bool,

  /** Tooltip placement relative to the trigger (automatically changes position when near a screen edge). Note: On mobile, the popover displays below the trigger by default, and adjusts to above the trigger if necessary. */
  placement: PropTypes.oneOf([
    'top',
    'top-start',
    'top-end',
    'right',
    'right-start',
    'right-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'left',
    'left-start',
    'left-end',
  ]),

  /** Preventing overflow while maintaining the desired placement as best as possible. */
  shiftElement: PropTypes.bool,
};

Popover.defaultProps = {
  isOpen: false,
  placement: 'right',
  hideArrow: false,
  shiftElement: false,
};

export default Popover;
